// Click a heading, sort the table by it. Owner, 2026-08-19: *"add to each table
// in the dashboard that shows up teh ability to sort each colum by clicking on
// nheading"*.
//
// ONE implementation for every table in the app. Twenty-five tables each with
// their own click handler is twenty-five chances for one of them to sort
// "10.00" before "9.00", or to lose a row with a blank cell.
//
// THE COMPARISON RULES, decided once, here:
//   - a value that is a NUMBER (or a money string like "1,234.50") sorts
//     numerically, never as text. Text sorting is what puts $9 above $10.
//   - an ISO date or timestamp sorts chronologically, by string, which for ISO
//     is the same thing and avoids constructing a Date per comparison.
//   - everything else sorts as text, case-insensitively, using localeCompare
//     with numeric collation so "Week 9" precedes "Week 10".
//   - BLANKS ALWAYS SINK. Null, undefined and '' go to the bottom in both
//     directions — a person sorting a column is looking for the extremes of
//     the values, and a wall of blanks at the top is not an answer.
//   - the sort is STABLE, so rows that tie stay in the order the server sent,
//     which is usually the meaningful one (submitted order, roster order).
//
// The sort NEVER changes what a table contains — no filtering, no grouping,
// no hiding a voided row. It reorders, and that is all.
import { useMemo, useState } from 'preact/hooks';

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  key: string | null;
  direction: SortDirection;
}

/** A money-ish string: optional currency, thousands separators, decimals,
 * and the accounting parentheses this app uses for deductions. */
const MONEYISH = /^\(?\s*-?\s*[$]?\s*\d{1,3}(,\d{3})*(\.\d+)?\s*\)?$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]|$)/;

function toNumber(v: string): number | null {
  if (!MONEYISH.test(v.trim())) return null;
  const negative = /^\(.*\)$/.test(v.trim());
  const n = Number(v.replace(/[(),$\s]/g, ''));
  if (!Number.isFinite(n)) return null;
  return negative ? -Math.abs(n) : n;
}

/** Null, undefined and empty string are all "blank" and all sink. */
function isBlank(v: unknown): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
}

export function compareValues(a: unknown, b: unknown): number {
  const aBlank = isBlank(a);
  const bBlank = isBlank(b);
  if (aBlank && bBlank) return 0;
  // Blanks sink in BOTH directions, so they are handled before the direction
  // is applied — see sortRows.
  if (aBlank) return 1;
  if (bBlank) return -1;

  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Number(Boolean(b)) - Number(Boolean(a));
  }
  if (typeof a === 'number' && typeof b === 'number') return a - b;

  const as = String(a);
  const bs = String(b);

  if (ISO_DATE.test(as) && ISO_DATE.test(bs)) return as < bs ? -1 : as > bs ? 1 : 0;

  const an = typeof a === 'number' ? a : toNumber(as);
  const bn = typeof b === 'number' ? b : toNumber(bs);
  if (an !== null && bn !== null) return an - bn;

  return as.localeCompare(bs, undefined, { numeric: true, sensitivity: 'base' });
}

export type Accessor<T> = (row: T) => unknown;

/**
 * Sort `rows` by `state`. Returns the SAME array reference when no column is
 * chosen, so a table that has never been clicked renders exactly what the
 * server sent.
 */
export function sortRows<T>(
  rows: T[],
  state: SortState,
  accessors: Record<string, Accessor<T>>,
): T[] {
  if (!state.key) return rows;
  const get = accessors[state.key];
  if (!get) return rows;

  // Decorate with the original index to make the sort stable across engines.
  const decorated = rows.map((row, index) => ({ row, index }));
  const factor = state.direction === 'desc' ? -1 : 1;
  decorated.sort((x, y) => {
    const a = get(x.row);
    const b = get(y.row);
    const aBlank = isBlank(a);
    const bBlank = isBlank(b);
    // Blanks sink regardless of direction. Applying `factor` to them would
    // float every empty cell to the top on the second click, which reads as
    // "the sort broke".
    if (aBlank || bBlank) {
      if (aBlank && bBlank) return x.index - y.index;
      return aBlank ? 1 : -1;
    }
    const c = compareValues(a, b);
    return c !== 0 ? c * factor : x.index - y.index;
  });
  return decorated.map((d) => d.row);
}

/** ▲ / ▼ / nothing — and an aria-sort value for the `<th>`. */
export function ariaSort(state: SortState, key: string): 'ascending' | 'descending' | 'none' {
  if (state.key !== key) return 'none';
  return state.direction === 'asc' ? 'ascending' : 'descending';
}

/**
 * The hook every table uses.
 *
 * `accessors` maps a column key to the value that column sorts on — which is
 * deliberately NOT always the value rendered: an amount column renders
 * "(20.00)" and sorts on -20, a name column renders a fallback like
 * "Employee #12" and sorts on the real name.
 *
 * Clicking a new column sorts it ascending; clicking the current column flips
 * it; a third click clears the sort and hands back the server's own order,
 * because "put it back how it was" is otherwise impossible without a reload.
 */
export function useTableSort<T>(
  rows: T[],
  accessors: Record<string, Accessor<T>>,
  initial: SortState = { key: null, direction: 'asc' },
): {
  rows: T[];
  state: SortState;
  toggle: (key: string) => void;
} {
  const [state, setState] = useState<SortState>(initial);

  const sorted = useMemo(() => sortRows(rows, state, accessors), [rows, state, accessors]);

  function toggle(key: string): void {
    setState((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: 'asc' };
    });
  }

  return { rows: sorted, state, toggle };
}
